import React, { FC, useEffect, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import Loader from 'src/utils/Loader'
import { useAppDispatch, useAppSelector } from 'src/redux/storeHooks'
import {
  getPlatformChildFieldDataCategoriesAsync,
  getSelectedCategoryTreeAsync,
  getFieldTreeSearchAsync,
  refreshPlatformFieldDataAttributesAsync,
} from 'src/redux/actions/templateAndPricingActions'
import { getPlatformFieldsAsync } from 'src/redux/actions/productActions'
import { AttributeType, CATEGORY_ITEM_ON_UPDATE, Category } from 'src/interfaces/Template.interface'
import { PlatformDataType } from 'src/interfaces/Platform.interface'
import { buildFieldTree, FlatFieldTreeItem } from 'src/utils/buildFieldTree'
import { useAttributePolling } from 'src/hooks/useAttributePolling'
import { useProgressiveLoadingMessage } from 'src/marketConnect/helpers'
import InputAttributes from './InputAttributes.previous'

export interface CategoryMappingChangeData {
  categoryId: number | null
  categoryPath: string
  isLeafCategory: boolean
  showAttributes: boolean
  isAttributeLoading: boolean
}

export interface CategoryMappingEngineProps {
  selectedSecondaryPlatform: PlatformDataType | null
  isEditMode?: boolean
  initialCategoryId?: number | null
  initialCategoryPath?: string
  initialPlatformFieldId?: number | null
  updatingMapData?: CATEGORY_ITEM_ON_UPDATE | null
  inputAttributesKey?: string
  onCategoryChange?: (data: CategoryMappingChangeData) => void
  onAttributeSelection: (attributes: any[]) => void
  nativeFields?: any[]
  customFields?: any[]
}

const TREE_PAGE_LIMIT = 30

export const MANUAL_MAP_PLATFORMS = [
  'DEBENHAMS',
  'SUPERPHARM',
  'CATCH',
  'THERANGEPLUS',
  'VINTED',
  'EBAY',
  'ETSY',
  'TIKTOK',
  'BACKMARKET',
  'POSHMARK',
  'TEMU',
  'LAZADA',
  'VESTIAIRECOLLECTIVE',
  'CDISCOUNT',
  'SHOPIFY',
  'BIGCOMMERCE',
  'OnBuy',
  'SHEIN',
  'ZALORA',
  'WHATNOT',
  'JUMIAVENDOR',
  'KOGAN','KOGANNZD',
  'TARGETPLUS',
  'FAIRE',
  'FRUUGO',
  'FRUUGOV2',
  'WALMART',
  'WIX',
  'GUNBROKER',
]

const CategoryMappingEngine: FC<CategoryMappingEngineProps> = ({
  selectedSecondaryPlatform,
  isEditMode = false,
  initialCategoryId = null,
  initialCategoryPath = '',
  initialPlatformFieldId = null,
  updatingMapData = null,
  inputAttributesKey,
  onCategoryChange,
  onAttributeSelection,
  nativeFields,
  customFields,
}) => {
  type TreeNode = import('src/utils/buildFieldTree').FieldTreeNode

  const dispatch = useAppDispatch()
  const { getAttributeData } = useAppSelector((state) => state.templateAndPricing)

  const {
    isAttributeLoading,
    showAttributes,
    setShowAttributes,
    fetchAttributesForCategory,
    clearPolling,
  } = useAttributePolling(selectedSecondaryPlatform)

  const attributeLoadingMessage = useProgressiveLoadingMessage(isAttributeLoading)
  const needsManualMapButton = MANUAL_MAP_PLATFORMS.includes(
    selectedSecondaryPlatform?.code || ''
  )
  const selectedPlatformCode = (
    selectedSecondaryPlatform?.code ||
    selectedSecondaryPlatform?.name ||
    selectedSecondaryPlatform?.platform_info?.name ||
    selectedSecondaryPlatform?.display_name ||
    ''
  ).toUpperCase()
  const isCdiscountPlatform = selectedPlatformCode === 'CDISCOUNT'


  const [categoryMainId, setCategoryMainId] = useState<number | null>(null)
  const [isLeafCategory, setIsLeafCategory] = useState(false)
  const [selectedCategoryIdForMapping, setSelectedCategoryIdForMapping] = useState<number | null>(
    null
  )
  const [treeData, setTreeData] = useState<TreeNode[]>([])
  const [treeSearch, setTreeSearch] = useState('')
  const [treeSelectedId, setTreeSelectedId] = useState<number | null>(null)
  const [treeSelectedPath, setTreeSelectedPath] = useState<string>('')
  const [isTreeOpen, setIsTreeOpen] = useState(false)
  const [treeSearchResults, setTreeSearchResults] = useState<Category[]>([])
  const [treeSearchLoading, setTreeSearchLoading] = useState(false)
  const [treeSearchPage, setTreeSearchPage] = useState(1)
  const [treeSearchHasMore, setTreeSearchHasMore] = useState(false)
  const [treeSearchLoadingMore, setTreeSearchLoadingMore] = useState(false)
  const [treePage, setTreePage] = useState(1)
  const [treeHasMore, setTreeHasMore] = useState(true)
  const [treeLoadingMore, setTreeLoadingMore] = useState(false)
  const [refreshAttributesLoading, setRefreshAttributesLoading] = useState(false)
  const [refreshAttributesError, setRefreshAttributesError] = useState<string>('')
  const [selectedCategoryAttributeSetId, setSelectedCategoryAttributeSetId] = useState<
    string | number | boolean | null | undefined
  >(undefined)


  const treeDropdownRef = useRef<HTMLDivElement | null>(null)
  const treeSearchDebounce = useRef<NodeJS.Timeout | null>(null)
  const flatTreeResultsRef = useRef<FlatFieldTreeItem[]>([])
  const treeSearchRef = useRef('')
  const onCategoryChangeRef = useRef(onCategoryChange)
  const editHydratedRef = useRef(false)

  // ΓöÇΓöÇ style helpers ΓöÇΓöÇ
  const treeContainerStyle: React.CSSProperties = {
    border: '1px solid #d0d7de',
    borderRadius: 8,
    padding: 8,
    boxShadow: '0 1px 3px rgba(15,23,42,0.08)',
    background: '#fff',
  }

  const nodeRowStyle = (isSelected: boolean): React.CSSProperties => ({
    padding: '4px 6px',
    borderRadius: 4,
    cursor: 'pointer',
    backgroundColor: isSelected ? '#e7f1ff' : 'transparent',
  })

  // tree helpers

  const updateNode = (
    nodes: TreeNode[],
    id: number,
    updater: (n: TreeNode) => TreeNode
  ): TreeNode[] =>
    nodes.map((n) =>
      n.id === id
        ? updater(n)
        : { ...n, children: n.children ? updateNode(n.children, id, updater) : n.children }
    )

  const findNodeById = (nodes: TreeNode[], id: number): TreeNode | undefined => {
    for (const n of nodes) {
      if (n.id === id) return n
      if (n.children?.length) {
        const found = findNodeById(n.children, id)
        if (found) return found
      }
    }
    return undefined
  }

  const mergeUniqueById = <T extends { id: number }>(currentItems: T[], nextItems: T[]) =>
    [...currentItems, ...nextItems].filter(
      (item, idx, arr) => arr.findIndex((candidate) => candidate.id === item.id) === idx
    )

  const renderHighlightedText = (text: string, term?: string) => {
    const trimmedTerm = term?.trim()
    if (!trimmedTerm) return text

    const escapedTerm = trimmedTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'ig'))
    const lowerTerm = trimmedTerm.toLowerCase()

    return parts.map((part, index) =>
      part.toLowerCase() === lowerTerm ? (
        <mark key={`${part}-${index}`} className='px-1 py-0 rounded bg-warning bg-opacity-25'>
          {part}
        </mark>
      ) : (
        <React.Fragment key={`${part}-${index}`}>{part}</React.Fragment>
      )
    )
  }

  const loadTree = async (
    platformId: number,
    platformFieldId: number,
    opts?: { selectedChildId?: number; searchTerm?: string; page?: number }
  ) => {
    const page = opts?.page ?? 1
    const limit = TREE_PAGE_LIMIT
    const res: any = await dispatch(
      getFieldTreeSearchAsync({
        categoryId: platformId,
        platform_field_id: platformFieldId,
        search_string: opts?.searchTerm ?? '',
        page,
        limit,
        use_pagination: true,
        selected_child_id: opts?.selectedChildId,
      })
    )
    const results: FlatFieldTreeItem[] = res?.payload?.results || res?.payload || []
    const merged =
      page === 1 ? results : mergeUniqueById(flatTreeResultsRef.current, results)
    flatTreeResultsRef.current = merged
    setTreeData(buildFieldTree(merged))
    setTreePage(page)
    setTreeHasMore(Boolean(res?.payload?.next))
  }

  const loadSearchTree = async (
    platformId: number,
    platformFieldId: number,
    searchTerm: string,
    page = 1
  ) => {
    const res: any = await dispatch(
      getFieldTreeSearchAsync({
        categoryId: platformId,
        platform_field_id: platformFieldId,
        search_string: searchTerm,
        page,
        limit: TREE_PAGE_LIMIT,
        use_pagination: true,
      })
    )

    if (treeSearchRef.current.trim() !== searchTerm) return

    const results: Category[] = res?.payload?.results || res?.payload || []
    setTreeSearchResults((prev) =>
      page === 1 ? results : mergeUniqueById(prev, results)
    )
    setTreeSearchPage(page)
    setTreeSearchHasMore(Boolean(res?.payload?.next))
  }

  const handleTreeScroll = async (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget
    if (target.scrollTop + target.clientHeight < target.scrollHeight - 20) return

    const platformId = selectedSecondaryPlatform?.id
    if (!platformId || !categoryMainId) return

    const searchTerm = treeSearch.trim()
    const isSearchActive = searchTerm.length >= 2

    if (isSearchActive) {
      if (!treeSearchHasMore || treeSearchLoading || treeSearchLoadingMore) return
      setTreeSearchLoadingMore(true)
      try {
        await loadSearchTree(platformId, categoryMainId, searchTerm, treeSearchPage + 1)
      } finally {
        setTreeSearchLoadingMore(false)
      }
      return
    }

    if (!treeHasMore || treeLoadingMore) return
    setTreeLoadingMore(true)
    try {
      await loadTree(platformId, categoryMainId, { page: treePage + 1 })
    } finally {
      setTreeLoadingMore(false)
    }
  }

  const loadChildren = async (nodeId: number) => {
    if (!categoryMainId) return []
    setTreeData((prev) => updateNode(prev, nodeId, (n) => ({ ...n, loading: true })))
    const res: any = await dispatch(
      getPlatformChildFieldDataCategoriesAsync({
        platform_Id: nodeId,
        search_string: '',
        page: 1,
        limit: 10,
        use_pagination: true,
        platform_field_id: categoryMainId,
      })
    )
    const kids: TreeNode[] = res?.payload?.results
      ? res.payload.results.map((k: Category) => ({ ...k, children: [], hasFetched: false }))
      : []
    setTreeData((prev) =>
      updateNode(prev, nodeId, (n) => ({
        ...n,
        loading: false,
        isExpanded: true,
        hasFetched: true,
        children: kids,
      }))
    )
    return kids
  }

  const buildTreeFromSearchResults = (results: any[]): TreeNode[] => {
    let syntheticId = -1
    const roots: TreeNode[] = []

    const findOrCreate = (
      list: TreeNode[],
      label: string,
      parentId: number | null,
      baseData?: any,
      isLeafOverride?: boolean
    ): TreeNode => {
      let node = list.find(
        (n) => n.api_name === label && (parentId === null || n.parent_data === parentId)
      )
      if (!node) {
        node = {
          id: baseData?.id ?? syntheticId--,
          api_name: label,
          api_id: baseData?.api_id ?? '',
          api_code: baseData?.api_code ?? '',
          attribute_set_id: baseData?.attribute_set_id,
          updated_at: baseData?.updated_at ?? '',
          created_at: baseData?.created_at ?? '',
          platform: baseData?.platform ?? selectedSecondaryPlatform?.id ?? 0,
          platform_field: baseData?.platform_field ?? categoryMainId ?? 0,
          parent_data: baseData?.parent_data ?? parentId,
          child_data: [],
          children: [],
          hasFetched: true,
          isExpanded: true,
          loading: false,
          isLeaf: Boolean(isLeafOverride),
        }
        list.push(node)
      }
      node.isExpanded = true
      return node
    }

    results.forEach((item) => {
      const parts = item?.api_name
        ? item.api_name
          .split('->')
          .map((p: string) => p.trim())
          .filter(Boolean)
        : []
      if (!parts.length) return
      let currentList = roots
      let parentId: number | null = null
      parts.forEach((label: string, idx: number) => {
        const isLeaf = idx === parts.length - 1
        const baseData = isLeaf ? item : undefined
        const node = findOrCreate(currentList, label, parentId, baseData, isLeaf)
        if (isLeaf) {
          node.id = item.id
          node.api_id = item.api_id ?? node.api_id
          node.api_code = item.api_code ?? node.api_code
          node.attribute_set_id = item.attribute_set_id
          node.parent_data = item.parent_data ?? node.parent_data
          node.isLeaf = true
        }
        if (!node.children) node.children = []
        currentList = node.children
        parentId = node.id
      })
    })

    return roots
  }

  const buildTreeFromPathNodes = (pathNodes: Category[]): TreeNode[] => {
    const roots: TreeNode[] = []
    let prevNode: TreeNode | null = null
    pathNodes.forEach((p, idx) => {
      const node: TreeNode = {
        ...p,
        children: [],
        hasFetched: true,
        isExpanded: true,
        loading: false,
        isLeaf: idx === pathNodes.length - 1,
      }
      if (idx === 0) {
        roots.push(node)
      } else if (prevNode) {
        prevNode.children = prevNode.children || []
        prevNode.children.push(node)
      }
      prevNode = node
    })
    return roots
  }

  const reloadFullCategoryTree = () => {
    const platformId = selectedSecondaryPlatform?.id
    if (!platformId || !categoryMainId) {
      setTreeData(buildFieldTree(flatTreeResultsRef.current))
      return
    }
    setTreePage(1)
    setTreeHasMore(true)
    loadTree(platformId, categoryMainId, { page: 1 })
  }

  const handleClearCategory = (e?: React.MouseEvent) => {
    e?.stopPropagation()
    setSelectedCategoryIdForMapping(null)
    setSelectedCategoryAttributeSetId(null)
    setTreeSelectedPath('')
    setTreeSelectedId(null)
    setShowAttributes(false)
    setIsLeafCategory(false)
    setTreeSearch('')
    onAttributeSelection([])
    reloadFullCategoryTree()
  }

  const handleTreeSelect = async (node: TreeNode) => {
    if (node.id !== selectedCategoryIdForMapping) {
      onAttributeSelection([])
    }
    setTreeSelectedId(node.id)
    setShowAttributes(false)
    setIsLeafCategory(false)
    setSelectedCategoryIdForMapping(node.id)
    setSelectedCategoryAttributeSetId(node.attribute_set_id ?? node.item?.attribute_set_id)

    try {
      const pathRes: any = await dispatch(
        getSelectedCategoryTreeAsync({ category_data_id: node.id })
      )
      const path: Category[] = pathRes?.payload || []
      if (path.length) {
        setTreeSelectedPath(path.map((p) => p.api_name).join(' > '))
        setTreeData(buildTreeFromPathNodes(path))
      } else if (node.api_name) {
        setTreeSelectedPath(node.api_name)
      }
    } catch (err) {
      if (node.api_name) setTreeSelectedPath(node.api_name)
    }

    const children = node.hasFetched ? node.children || [] : await loadChildren(node.id)
    if (!children || children.length === 0) {
      setIsLeafCategory(true)
      fetchAttributesForCategory(node.id)
    }
    setTreeSearch('')
    setIsTreeOpen(false)
  }

  const handleNodeClick = async (node: TreeNode) => {
    if (node.loading) return
    if (node.children && node.children.length > 0) {
      setTreeData((prev) =>
        updateNode(prev, node.id, (n) => ({
          ...n,
          isExpanded: !n.isExpanded,
          children: n.children || [],
          isLeaf: false,
        }))
      )
      return
    }

    if (!node.hasFetched) {
      const kids = await loadChildren(node.id)
      if (kids.length) {
        setTreeData((prev) =>
          updateNode(prev, node.id, (n) => ({
            ...n,
            isExpanded: true,
            hasFetched: true,
            children: kids as any,
            isLeaf: false,
          }))
        )
        return
      }
    }

    await handleTreeSelect(node)
  }

  const renderTree = (nodes: TreeNode[], depth: number, term?: string) => {
    const list =
      term
        ? buildTreeFromSearchResults(treeSearchResults)
        : nodes

    const attachState = (items: TreeNode[]): TreeNode[] =>
      items.map((n) => {
        const stateNode = findNodeById(treeData, n.id)
        const merged = stateNode ? { ...n, ...stateNode } : n
        return { ...merged, children: n.children ? attachState(n.children) : [] }
      })

    const viewList = term ? list : attachState(list)

    const renderNodes = (items: TreeNode[], level: number): JSX.Element[] =>
      items.map((node) => (
        <div key={node.id} className='mb-1'>
          <div className='d-flex align-items-center'>
            {Array.from({ length: level }).map((_, idx) => (
              <span
                key={idx}
                style={{
                  width: 14,
                  borderLeft: '1px solid #d0d7de',
                  marginRight: 6,
                  height: '100%',
                }}
              ></span>
            ))}
            <Button
              variant='outline-secondary'
              size='sm'
              className='p-0 me-2'
              type='button'
              style={{ width: 22, height: 22, lineHeight: '20px', fontSize: 12 }}
              onClick={async () => {
                if (node.loading) return
                if (node.hasFetched && node.children && node.children.length > 0) {
                  setTreeData((prev) =>
                    updateNode(prev, node.id, (n) => ({ ...n, isExpanded: !n.isExpanded }))
                  )
                  return
                }
                if (!node.hasFetched) {
                  const kids = await loadChildren(node.id)
                  if ((kids as any)?.length) {
                    setTreeData((prev) =>
                      updateNode(prev, node.id, (n) => ({
                        ...n,
                        children: kids as any,
                        hasFetched: true,
                        isExpanded: true,
                        isLeaf: false,
                      }))
                    )
                  }
                  return
                }
              }}
            >
              {node.loading
                ? '...'
                : node.children && node.children.length
                  ? node.isExpanded
                    ? '-'
                    : '+'
                  : node.hasFetched && (node.children?.length ?? 0) === 0
                    ? '-'
                    : '+'}
            </Button>
            <span
              role='button'
              className={`flex-grow-1 ${treeSelectedId === node.id ? 'fw-semibold text-primary' : ''
                }`}
              style={nodeRowStyle(treeSelectedId === node.id)}
              onClick={() => handleNodeClick(node)}
            >
              {renderHighlightedText(node.api_name, term)}
            </span>
          </div>
          {node.isExpanded && node.children?.length ? (
            <div style={{ width: '100%' }}>{renderNodes(node.children, level + 1)}</div>
          ) : null}
        </div>
      ))

    return renderNodes(viewList, depth)
  }

  // ΓöÇΓöÇ utility ΓöÇΓöÇ

  function isErrorWithMessage(
    data: AttributeType[] | { message: string; queue_id?: string }
  ): data is { message: string; queue_id?: string } {
    const candidate = data as { message?: string; queue_id?: string }
    return Boolean(candidate?.message && !candidate?.queue_id)
  }

  const handleAttributeSelection = (attributes: any[]) => {
    onAttributeSelection(attributes)
  }

  const shouldShowRefreshAttributesButton =
    isEditMode && selectedPlatformCode === 'SHEIN' && selectedCategoryIdForMapping !== null

  const variantProductMessage =
    selectedCategoryAttributeSetId === true ||
    String(selectedCategoryAttributeSetId ?? '').trim().toLowerCase() === 'true'
      ? 'Variant Products is allowed for this Category'
      : 'Variant Products is not allowed for this Category'

  const getRefreshAttributesErrorMessage = (errorData: any) => {
    const apiMessage =
      errorData?.massage ||
      errorData?.message ||
      errorData?.data?.massage ||
      errorData?.data?.message ||
      errorData?.error?.massage ||
      errorData?.error?.message

    return apiMessage
      ? `Unable to refresh attributes. ${apiMessage}`
      : 'Unable to refresh attributes.'
  }

  const handleRefreshAttributes = async () => {
    if (selectedCategoryIdForMapping === null) return
    setRefreshAttributesLoading(true)
    setRefreshAttributesError('')
    try {
      const response: any = await dispatch(
        refreshPlatformFieldDataAttributesAsync({
          platform_field_data_id: selectedCategoryIdForMapping,
          platform_id: selectedSecondaryPlatform?.id,
        })
      )
      if (response.meta?.requestStatus === 'rejected') {
        setRefreshAttributesError(
          getRefreshAttributesErrorMessage(response.payload || response.error)
        )
        return
      }
      toast.success('Attributes refresh started successfully')
      fetchAttributesForCategory(selectedCategoryIdForMapping)
    } catch (error) {
      setRefreshAttributesError(getRefreshAttributesErrorMessage(error))
    } finally {
      setRefreshAttributesLoading(false)
    }
  }



  // Keep onCategoryChangeRef current
  useEffect(() => {
    onCategoryChangeRef.current = onCategoryChange
  }, [onCategoryChange])

  useEffect(() => {
    treeSearchRef.current = treeSearch.trim()
  }, [treeSearch])

  // Fetch category tree when creating, or when editing without a hydrated category
  useEffect(() => {
    const platformId = selectedSecondaryPlatform?.id
    if (!platformId) return
    if (isEditMode && initialCategoryId && initialPlatformFieldId) return
    dispatch(
      getPlatformFieldsAsync({
        platformId,
        otherParams: { field_code: 'CATEGORY' },
      })
    ).then((response) => {
      const fieldId = response?.payload?.[0]?.id
      if (!fieldId) return
      setCategoryMainId(fieldId)
      setTreePage(1)
      setTreeHasMore(true)
      loadTree(platformId, fieldId, { page: 1 })
    })
  }, [selectedSecondaryPlatform?.id, isEditMode, initialCategoryId, initialPlatformFieldId])

  // Initialize from props in EDIT mode (once per mount, so a later user clear is not overwritten)
  useEffect(() => {
    if (!isEditMode || !initialCategoryId || !initialPlatformFieldId) return
    if (editHydratedRef.current) return
    editHydratedRef.current = true
    setCategoryMainId(initialPlatformFieldId)
    setTreeSelectedId(initialCategoryId)
    setTreeSelectedPath(initialCategoryPath || '')
    setSelectedCategoryIdForMapping(initialCategoryId)
    setSelectedCategoryAttributeSetId(
      (updatingMapData?.category_data as any)?.attribute_set_id
    )
    if (selectedSecondaryPlatform?.id) {
      loadTree(selectedSecondaryPlatform.id, initialPlatformFieldId, {
        selectedChildId: initialCategoryId,
        page: 1,
      })
    }
    fetchAttributesForCategory(initialCategoryId)
  }, [isEditMode, initialCategoryId, initialPlatformFieldId])

  // Click outside closes tree dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        treeDropdownRef.current &&
        !treeDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTreeOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Debounced tree search
  useEffect(() => {
    if (treeSearchDebounce.current) clearTimeout(treeSearchDebounce.current)
    const searchTerm = treeSearch.trim()
    if (!searchTerm || searchTerm.length < 2) {
      setTreeSearchResults([])
      setTreeSearchLoading(false)
      setTreeSearchPage(1)
      setTreeSearchHasMore(false)
      setTreeSearchLoadingMore(false)
      return
    }
    const searchRootId = selectedSecondaryPlatform?.id
    if (!searchRootId || !categoryMainId) {
      setTreeSearchLoading(false)
      return
    }
    treeSearchDebounce.current = setTimeout(() => {
      setTreeSearchLoading(true)
      setTreeSearchResults([])
      setTreeSearchPage(1)
      setTreeSearchHasMore(false)
      setTreeSearchLoadingMore(false)
      loadSearchTree(searchRootId, categoryMainId, searchTerm, 1)
        .finally(() => setTreeSearchLoading(false))
    }, 500)
    return () => {
      if (treeSearchDebounce.current) clearTimeout(treeSearchDebounce.current)
    }
  }, [treeSearch, categoryMainId, selectedSecondaryPlatform?.id, dispatch])

  // Notify parent when relevant state changes
  useEffect(() => {
    onCategoryChangeRef.current?.({
      categoryId: selectedCategoryIdForMapping,
      categoryPath: treeSelectedPath,
      isLeafCategory,
      showAttributes,
      isAttributeLoading,
    })
  }, [
    selectedCategoryIdForMapping,
    treeSelectedPath,
    isLeafCategory,
    showAttributes,
    isAttributeLoading,
  ])

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      clearPolling()
    }
  }, [])

  const savedCategoryId =
    initialCategoryId ??
    (typeof updatingMapData?.category_data === 'object'
      ? (updatingMapData.category_data as any)?.id
      : updatingMapData?.category_data)
  const shouldUseSavedAttributeMappings =
    Boolean(updatingMapData) &&
    selectedCategoryIdForMapping != null &&
    savedCategoryId != null &&
    Number(selectedCategoryIdForMapping) === Number(savedCategoryId)

  return (
    <div>
      {/* Tree picker */}
      <div className='mb-8' ref={treeDropdownRef}>
        <label className='form-label'>Select Category</label>
        <div className='position-relative'>
          <div
            className='form-control d-flex align-items-center justify-content-between'
            role='button'
            onClick={() => {
              setIsTreeOpen((prev) => !prev)
            }}
            onFocus={() => {
              setIsTreeOpen(true)
            }}
          >
            <span className={treeSelectedPath ? '' : 'text-muted'}>
              {treeSelectedPath || 'Search categories'}
            </span>
            <div className='d-flex align-items-center'>
              {treeSelectedPath && (
                <i
                  className='bi bi-x-circle me-2 text-muted'
                  role='button'
                  title='Clear selection'
                  style={{ fontSize: '0.9rem' }}
                  onClick={handleClearCategory}
                />
              )}
              <i className='bi bi-search me-2 text-muted'></i>
              <i
                className={`bi ${isTreeOpen ? 'bi-chevron-up' : 'bi-chevron-down'
                  } text-muted`}
              ></i>
            </div>
          </div>

          {isTreeOpen && (
            <div
              className='position-absolute w-100 bg-white mt-1'
              style={{
                zIndex: 10,
                border: '1px solid #d0d7de',
                borderRadius: 8,
                boxShadow: '0 6px 18px rgba(15,23,42,0.12)',
              }}
            >
              <div className='p-2 border-bottom'>
                <div className='position-relative'>
                  <input
                    className='form-control'
                    placeholder='Search categories'
                    value={treeSearch}
                    onChange={(e) => setTreeSearch(e.target.value)}
                    autoFocus
                  />
                  <i
                    className='bi bi-search position-absolute'
                    style={{
                      right: 10,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6c757d',
                    }}
                  ></i>
                </div>
                {treeSearchLoading && treeSearchResults.length === 0 && (
                  <div className='d-flex align-items-center gap-2 mt-2 text-muted'>
                    <Spinner animation='border' size='sm' /> <span>SearchingΓÇª</span>
                  </div>
                )}
              </div>
              <div
                style={{
                  maxHeight: 320,
                  overflow: 'auto',
                  ...treeContainerStyle,
                  border: 'none',
                  boxShadow: 'none',
                  borderRadius: '0 0 8px 8px',
                }}
                onScroll={handleTreeScroll}
              >
                {treeSearch.trim().length >= 2 &&
                  !treeSearchLoading &&
                  treeSearchResults.length === 0 ? (
                  <div className='text-muted px-2 py-3'>No results found</div>
                ) : null}
                {renderTree(
                  treeData,
                  0,
                  treeSearch.trim().length >= 2 ? treeSearch.trim() : undefined
                )}
                {(treeLoadingMore || treeSearchLoadingMore) && (
                  <div className='text-center py-2 text-muted small'>Loading more...</div>
                )}
              </div>
            </div>
          )}
        </div>
        {isCdiscountPlatform && selectedCategoryIdForMapping !== null && (
          <div className='form-text mt-2'>
            Note : {variantProductMessage}
          </div>
        )}
      </div>

      {/* Map Attributes button (manual trigger platforms only) */}
      {(needsManualMapButton || shouldShowRefreshAttributesButton) && (
        <div className='m-3 text-center'>
          <div className='d-inline-flex gap-3 align-items-center'>
            {needsManualMapButton && (
              <Button
                type='button'
                disabled={selectedCategoryIdForMapping == null || isAttributeLoading}
                onClick={() => fetchAttributesForCategory(selectedCategoryIdForMapping)}
              >
                Map Attributes
              </Button>
            )}
            {shouldShowRefreshAttributesButton && (
              <Button
                type='button'
                variant='secondary'
                disabled={selectedCategoryIdForMapping == null || refreshAttributesLoading}
                onClick={handleRefreshAttributes}
              >
                {refreshAttributesLoading ? 'Refreshing...' : 'Refresh Attributes'}
              </Button>
            )}
          </div>
          {refreshAttributesError && (
            <div className='text-danger mt-3'>{refreshAttributesError}</div>
          )}
        </div>
      )}


      {isAttributeLoading && (
        <div className='d-flex flex-column align-items-center text-center my-4 text-secondary'>
          <Loader variant="section" />
          {attributeLoadingMessage && (
            <div className='mt-3'>{attributeLoadingMessage}</div>
          )}
        </div>
      )}

      {showAttributes &&
        (isErrorWithMessage(getAttributeData) ? (
          <div className='d-flex flex-column align-items-center text-center my-4 text-secondary'>
            <i className='bi bi-exclamation-circle fs-4 mb-1'></i>
            <div className='fw-semibold fs-5'>
              {getAttributeData?.message || 'Something went wrong.'}
            </div>
          </div>
        ) : (
          <InputAttributes
            key={inputAttributesKey || String(selectedCategoryIdForMapping)}
            paltformId={selectedSecondaryPlatform?.id}
            categoryAttributes={getAttributeData}
            handleAttributeSelection={handleAttributeSelection}
            updatingMapData={shouldUseSavedAttributeMappings ? updatingMapData ?? null : null}
            nativeFields={nativeFields}
            customFields={customFields}
          />
        ))}
    </div>
  )
}

export default CategoryMappingEngine
